// Shared color helpers for the owned team-badge UI.
//
// Single source of truth for the chip-fill / text-contrast pipeline used by
// both TeamLogoImg (App.jsx + BracketMode call sites) and PickemMode.TeamLogo
// (pickem card team buttons). The duplicated copies that previously lived in
// each component were collapsed here; if anything in this file changes, both
// consumers pick it up automatically.
//
// Canonical pickLabel uses the rounded luminance — same value pickFill is
// already comparing against NEAR_BLACK_MAX, so the entire color-decision
// pipeline runs on one scale.

export const NEUTRAL_BG     = '#2a2a2a'
export const NEUTRAL_TEXT   = '#cccccc'
export const NEAR_BLACK_MAX = 24   // 0-255 luminance threshold below which we swap to alt

// Normalize "1d428a" / "#1d428a" / "" / null to "#1d428a" or null.
export function normalizeHex(h) {
  if (!h || typeof h !== 'string') return null
  const trimmed = h.trim().replace(/^#/, '')
  if (!/^[0-9a-fA-F]{6}$/.test(trimmed)) return null
  return '#' + trimmed.toLowerCase()
}

// 0-255 perceptual luminance (Rec. 709 weights). Math.round'd — the rounded
// value is what pickFill compares to NEAR_BLACK_MAX and what pickLabel
// compares to 140, so both decisions stay on the same scale.
export function luminance(hex) {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return Math.round(0.2126 * r + 0.7152 * g + 0.0722 * b)
}

// Pick the chip fill: primary, fall back to alternate if primary is missing
// or near-black, fall back to neutral if both are missing/near-black.
export function pickFill(color, alternateColor) {
  const primary = normalizeHex(color)
  const alt     = normalizeHex(alternateColor)
  if (primary && luminance(primary) > NEAR_BLACK_MAX) return primary
  if (alt     && luminance(alt)     > NEAR_BLACK_MAX) return alt
  return NEUTRAL_BG
}

// Iconic team color overrides — sport+abbr keys where ESPN's `color` field
// returns a navy/dark jersey color but the team's brand-iconic hue lives in
// `alternateColor`. Applied BEFORE pickFill, so for teams in this map the
// curated value short-circuits pickFill's near-black resolution; teams not
// in the map see no behavior change (pickFill keeps its current shape and
// its legitimate near-black handling for teams like PIT/SF).
//
// Adding a team here is a deliberate brand-identity decision. Verify the
// hue against the team's published brand guide — a wrong override is a
// silent wrong-color bug. Several teams (e.g., NYY) have navy as their
// genuine iconic color and do NOT belong here.
const ICONIC_COLOR_OVERRIDES = {
  // MLB — ESPN's `color` is the team's navy/dark jersey hue; iconic lives in alt.
  'MLB:BOS': '#bd3039',  // Red Sox red          (ESPN color: 0d2b56 navy)
  'MLB:HOU': '#eb6e1f',  // Astros orange        (ESPN color: 002d62 navy)
  'MLB:CLE': '#e31937',  // Guardians red        (ESPN color: 002b5c navy)
  'MLB:ATL': '#ba0c2f',  // Braves red           (ESPN color: 0c2340 navy)
  'MLB:MIN': '#e20e32',  // Twins red            (ESPN color: 031f40 navy)
  // NBA — modern Clippers brand emphasizes red; ESPN keeps navy as primary.
  'NBA:LAC': '#c8102e',  // Clippers red         (ESPN color: 12173f navy)
  // NHL — Bruins and Knights identity is gold; ESPN gives near-black / grey.
  'NHL:BOS': '#fdb71a',  // Bruins gold          (ESPN color: 231f20 near-black)
  'NHL:VGK': '#b4975a',  // Vegas gold           (ESPN color: 344043 grey)
}

// Returns the iconic team color for sport+abbr if curated, otherwise
// pickFill's existing primary/alternate/neutral resolution. Use this at
// any call site where the team's brand identity matters; bare pickFill is
// still correct for chip-fill sites that want ESPN's color as-is.
export function iconicTeamColor(sport, abbr, color, alternateColor) {
  const override = ICONIC_COLOR_OVERRIDES[`${sport}:${abbr}`]
  if (override) return override
  return pickFill(color, alternateColor)
}

// Pick label color: white on dark backgrounds, black on light. Threshold at
// mid luminance — chosen empirically against the existing team-color set.
export function pickLabel(bgHex) {
  return luminance(bgHex) > 140 ? '#0f0f0f' : '#ffffff'
}

// UFC fighter initial — single uppercase letter from the last whitespace-
// separated token of the fighter's name. "G. Bonfim" → "B", "Belal Muhammad"
// → "M", empty/undefined → "?". Used by every UFC chip render site
// (TeamLogoImg UFC branch, PickemMode.TeamLogo UFC branch, TeamComparison
// FighterSide) so all three stay in sync.
export function fighterInitial(name) {
  return (name || '?').trim().split(/\s+/).pop()?.[0]?.toUpperCase() || '?'
}
